import { useState, useEffect, useCallback } from "react"
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Checkbox,
  message,
  Row,
  Col,
} from "antd"
import { PlusOutlined, EditOutlined } from "@ant-design/icons"
import { db } from "../../../firebase/config"
import {
  doc,
  addDoc,
  updateDoc,
  collection,
  serverTimestamp,
  writeBatch,
  getDoc,
} from "firebase/firestore"
import { useCollection } from "../../../hooks/useCollection"

const { Option } = Select

export default function RollFormDrawer({
  materialData,
  materialLoading,
  forEditing = false,
  record = null,
}) {
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const { data: palletLocations, loading: palletLocationsLoading } =
    useCollection("palletLocations")

  // The calculatePrice function now updates the form field directly
  const calculatePrice = useCallback(
    (values) => {
      const { materialId, originalMeters, rollWidth } = values

      if (materialId && originalMeters > 0 && rollWidth > 0 && materialData) {
        const material = materialData.find((m) => m.id === materialId)
        const eurM2 = Number(material?.eurM2) || 0

        if (eurM2 > 0) {
          const widthInM = rollWidth / 1000
          const rawPrice = originalMeters * widthInM * eurM2
          const roundedPrice = Number(rawPrice.toFixed(2))

          // Update the 'price' field in the form
          form.setFieldsValue({ price: roundedPrice })
        }
      }
    },
    [materialData, form]
  )

  // useEffect is simplified, as the price is part of the record
  useEffect(() => {
    if (open && forEditing && record) {
      form.setFieldsValue({
        ...record,
        materialId: record.material?.id,
      })
    } else {
      form.resetFields()
    }
  }, [open, forEditing, record, form])

  const showDrawer = () => setOpen(true)
  const onClose = () => {
    setOpen(false)
    form.resetFields()
  }

  // handleFinish is simplified, as price is now part of the 'values' object
  const handleFinish = async (values) => {
    setLoading(true)
    const selectedMaterial = materialData.find((m) => m.id === values.materialId)

    const sanitizedValues = {
      ...values,
      verified: values.verified || false,
      shipping: values.shipping || false,
      inOrder: values.inOrder || false,
    }

    const rollData = {
      ...sanitizedValues,
      material: selectedMaterial || null,
      updated: serverTimestamp(),
    }

    try {
      if (forEditing) {
        const rollRef = doc(db, "rollStore", record.id)
        await updateDoc(rollRef, rollData)
        message.success(`Roll ${record.barcode || record.id} updated successfully.`)
      } else {
        await addDoc(collection(db, "rollStore"), {
          ...rollData,
          created: serverTimestamp(),
          inUse: false,
        })
        message.success("New roll created successfully.")
      }
      onClose()
    } catch (error) {
      console.error("Error saving roll: ", error)
      message.error("Failed to save roll. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleValuesChange = (changedValues, allValues) => {
    if (
      "materialId" in changedValues ||
      "originalMeters" in changedValues ||
      "rollWidth" in changedValues
    ) {
      calculatePrice(allValues)
    }
  }

  return (
    <>
      {forEditing ? (
        <Button size="small" icon={<EditOutlined />} onClick={showDrawer} />
      ) : (
        <Button type="primary" icon={<PlusOutlined />} onClick={showDrawer}>
          New Roll
        </Button>
      )}
      <Drawer
        title={forEditing ? `Edit Roll` : "Create New Roll"}
        width={450}
        onClose={onClose}
        open={open}
        bodyStyle={{ paddingBottom: 80 }}
        extra={
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={() => form.submit()} type="primary" loading={loading}>
              {forEditing ? "Save Changes" : "Submit"}
            </Button>
          </Space>
        }>
        <Form
          layout="vertical"
          style={{ width: "100%" }}
          form={form}
          onFinish={handleFinish}
          onValuesChange={handleValuesChange}>
          <Form.Item
            name="materialId"
            label="Material"
            rules={[{ required: true, message: "Please select a material" }]}>
            <Select placeholder="Select a material" loading={materialLoading} showSearch>
              {materialData?.map((material) => (
                <Option key={material.id} value={material.id}>
                  {material.material}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="barcode"
            label="Barcode"
            rules={[{ required: true, message: "Please enter a barcode" }]}>
            <Input placeholder="Enter barcode" />
          </Form.Item>

          <Form.Item name="palletLocation" label="Pallet Location">
            <Select placeholder="Select a pallet location" loading={palletLocationsLoading}>
              {palletLocations?.map((loc) => (
                <Option key={loc.id} value={loc.name}>
                  {loc.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="originalMeters"
                label="Original Meters"
                rules={[{ required: true, message: "Please enter original meters" }]}>
                <InputNumber style={{ width: "100%" }} placeholder="e.g., 2500" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="remainingMeters"
                label="Remaining Meters"
                rules={[{ required: true, message: "Please enter remaining meters" }]}>
                <InputNumber style={{ width: "100%" }} placeholder="e.g., 2500" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="rollWidth"
                label="Roll Width (mm)"
                rules={[{ required: true, message: "Please enter roll width" }]}>
                <InputNumber style={{ width: "100%" }} placeholder="e.g., 500" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="weight" label="Weight (kg)">
                <InputNumber style={{ width: "100%" }} placeholder="e.g., 75.5" step="0.01" />
              </Form.Item>
            </Col>
          </Row>

          {/* The price field is now an editable InputNumber */}
          <Form.Item name="price" label="Price (€)">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Price is auto-calculated"
              step="0.01"
              formatter={(value) => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/€\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item name="comment" label="Comment">
            <Input.TextArea rows={3} placeholder="Enter a comment" />
          </Form.Item>

          <Form.Item label="Status Flags">
            <Space direction="vertical">
              <Form.Item name="verified" valuePropName="checked" noStyle>
                <Checkbox>Verified in Stock</Checkbox>
              </Form.Item>
              <Form.Item name="shipping" valuePropName="checked" noStyle>
                <Checkbox>Shipping</Checkbox>
              </Form.Item>
              <Form.Item name="inOrder" valuePropName="checked" noStyle>
                <Checkbox>In Order</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
          {/* Ship Out Manually – only if roll is shipping with an order */}
          {/* {forEditing &&
            record?.id &&
            record.shipping &&
            record.inOrder &&
            record.fullOrderNumber && (
              <Form.Item>
                <Button
                  danger
                  block
                  onClick={async () => {
                    try {
                      // 1. Get roll data
                      const rollRef = doc(db, "rollStore", record.id)
                      const rollSnap = await getDoc(rollRef)
                      if (!rollSnap.exists()) {
                        message.error("Roll not found in store.")
                        return
                      }
                      const rollData = rollSnap.data()

                      // 2. Prepare batch
                      const batch = writeBatch(db)

                      const shippedRollData = {
                        ...rollData,
                        shippedAt: serverTimestamp(),
                        machine: rollData.destinationMachine || "Manual Shipment", // ✅ reuse destinationMachine
                      }

                      // add to "rolls"
                      const newRollRef = doc(collection(db, "rolls"))
                      batch.set(newRollRef, shippedRollData)

                      // add to "rollStoreHistory"
                      const newHistoryRef = doc(collection(db, "rollStoreHistory"))
                      batch.set(newHistoryRef, shippedRollData)

                      // delete from rollStore
                      batch.delete(rollRef)

                      // 3. Commit
                      await batch.commit()

                      message.success(
                        `Roll ${record.barcode || record.id} shipped out manually to ${
                          shippedRollData.machine
                        }.`
                      )
                      onClose()
                    } catch (err) {
                      console.error("Error shipping roll manually:", err)
                      message.error("Failed to ship roll manually.")
                    }
                  }}>
                  Ship Out Manually
                </Button>
              </Form.Item>
            )} */}

          {/* Refund Back – only if roll was marked back for refund */}
          {forEditing && record?.id && record.backForRefund === true && (
            <Form.Item>
              <Button
                type="primary"
                block
                onClick={async () => {
                  try {
                    const rollRef = doc(db, "rollStore", record.id)
                    await updateDoc(rollRef, {
                      verified: true,
                      shipping: false,
                      inOrder: false,
                      backForRefund: false,
                      updated: serverTimestamp(),
                    })
                    message.success(`Roll ${record.barcode || record.id} set back to In Stock.`)
                    onClose()
                  } catch (error) {
                    console.error("Error resetting roll status:", error)
                    message.error("Failed to reset roll status.")
                  }
                }}>
                Refund Back
              </Button>
            </Form.Item>
          )}
        </Form>
      </Drawer>
    </>
  )
}
